/**
 * RecipeCreateWizard — Unified recipe creation with tabs:
 * - "Generieren" = AI creates recipe from name + ingredients + notes
 * - "Manuell" = 3-step wizard (Basics → Ingredients with AI lookup → Steps)
 *
 * Replaces RecipeWizard + ManualRecipeWizard + RecipeModePicker
 */

import { useState, useEffect, useRef } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardSelect, WizardSuccess, WizardError } from './WizardModal';
import { post, get } from '../../api';
import { formatNumber } from '../../utils/formatNumber';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = 'generate' | 'manual';
type ManualStep = 'basics' | 'ingredients' | 'steps';

const CATEGORY_OPTIONS = [
  { value: 'breakfast', label: 'Frühstück' },
  { value: 'lunch', label: 'Mittagessen' },
  { value: 'dinner', label: 'Abendessen' },
  { value: 'snack', label: 'Snack' },
];

interface Ingredient {
  name: string;
  amount: number;
  unit: string;
  kcal: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
  fiber_g: number;
  b12_mcg: number;
  iron_mg: number;
  vitd_mcg: number;
  calcium_mg: number;
  zinc_mg: number;
  omega3_mg: number;
  iod_mcg: number;
}

interface Step {
  number: number;
  instruction: string;
  timer_seconds: number | null;
  timer_label: string | null;
}

export default function RecipeCreateWizard({ open, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('generate');

  // ── Shared state ──
  const [name, setName] = useState('');
  const [category, setCategory] = useState('dinner');
  const [servings, setServings] = useState('2');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [resultRecipe, setResultRecipe] = useState<{ name: string; kcal: number; protein_g: number; servings: number; prep_time_min?: number } | null>(null);

  // ── Generate mode ──
  const [genIngredients, setGenIngredients] = useState<{ name: string; amount: string }[]>([]);
  const [genIngInput, setGenIngInput] = useState('');
  const [genIngAmount, setGenIngAmount] = useState('');
  const [strictMode, setStrictMode] = useState(false);
  const [genNotes, setGenNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  // ── Manual mode ──
  const [manualStep, setManualStep] = useState<ManualStep>('basics');
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingName, setIngName] = useState('');
  const [ingAmount, setIngAmount] = useState('');
  const [ingUnit, setIngUnit] = useState('g');
  const [lookingUp, setLookingUp] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [stepText, setStepText] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Food item autocomplete ──
  interface FoodItemHit { id: number; name: string; kcal_per_100: number; protein_g_per_100: number; fat_g_per_100: number; carbs_g_per_100: number; fiber_g_per_100: number; b12_mcg_per_100: number; iron_mg_per_100: number; vitd_mcg_per_100: number; calcium_mg_per_100: number; zinc_mg_per_100: number; omega3_mg_per_100: number; iod_mcg_per_100: number }
  const [foodSuggestions, setFoodSuggestions] = useState<FoodItemHit[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchFoodItems = (query: string) => {
    if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
    if (query.length < 2) { setFoodSuggestions([]); setShowSuggestions(false); return; }
    suggestTimeoutRef.current = setTimeout(async () => {
      try {
        const items = await get<FoodItemHit[]>(`/food-items?q=${encodeURIComponent(query)}&limit=8`);
        setFoodSuggestions(items);
        setShowSuggestions(items.length > 0);
      } catch { setFoodSuggestions([]); }
    }, 200);
  };

  const selectFoodItem = (item: FoodItemHit) => {
    const amt = parseFloat(ingAmount) || 100;
    const factor = amt / 100;
    setIngredients(prev => [...prev, {
      name: item.name, amount: amt, unit: ingUnit,
      kcal: Math.round(item.kcal_per_100 * factor),
      protein_g: Math.round(item.protein_g_per_100 * factor * 10) / 10,
      fat_g: Math.round(item.fat_g_per_100 * factor * 10) / 10,
      carbs_g: Math.round(item.carbs_g_per_100 * factor * 10) / 10,
      fiber_g: Math.round((item.fiber_g_per_100 || 0) * factor * 10) / 10,
      b12_mcg: Math.round((item.b12_mcg_per_100 || 0) * factor * 100) / 100,
      iron_mg: Math.round((item.iron_mg_per_100 || 0) * factor * 100) / 100,
      vitd_mcg: Math.round((item.vitd_mcg_per_100 || 0) * factor * 100) / 100,
      calcium_mg: Math.round((item.calcium_mg_per_100 || 0) * factor * 10) / 10,
      zinc_mg: Math.round((item.zinc_mg_per_100 || 0) * factor * 100) / 100,
      omega3_mg: Math.round((item.omega3_mg_per_100 || 0) * factor * 10) / 10,
      iod_mcg: Math.round((item.iod_mcg_per_100 || 0) * factor * 10) / 10,
    }]);
    setIngName('');
    setIngAmount('');
    setShowSuggestions(false);
  };

  // ── Nutrition totals ──
  const totals = ingredients.reduce(
    (acc, ing) => ({
      kcal: acc.kcal + (ing.kcal || 0),
      protein_g: acc.protein_g + (ing.protein_g || 0),
      fat_g: acc.fat_g + (ing.fat_g || 0),
      carbs_g: acc.carbs_g + (ing.carbs_g || 0),
    }),
    { kcal: 0, protein_g: 0, fat_g: 0, carbs_g: 0 }
  );

  // ── Handlers: Generate mode ──

  const addGenIngredient = () => {
    if (!genIngInput.trim()) return;
    setGenIngredients(prev => [...prev, { name: genIngInput.trim(), amount: genIngAmount.trim() }]);
    setGenIngInput('');
    setGenIngAmount('');
  };

  const handleGenerate = async () => {
    if (!name.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const hasIngWithAmount = genIngredients.some(i => i.amount);
      const result = await post<{ recipe: { name: string; kcal: number; protein_g: number; servings: number; prep_time_min?: number } }>('/ai/generate-recipe', {
        name: name.trim(),
        category,
        servings: parseInt(servings) || 2,
        // If ingredients have amounts → use available_ingredients mode
        ...(hasIngWithAmount || strictMode
          ? {
              available_ingredients: genIngredients.map(i => ({ name: i.name, amount: i.amount || undefined })),
              ingredient_mode: strictMode ? 'strict' : 'flexible',
            }
          : {
              rough_ingredients: genIngredients.length > 0 ? genIngredients.map(i => i.name) : undefined,
            }
        ),
        notes: genNotes.trim() || undefined,
      });
      setResultRecipe(result.recipe);
      setDone(true);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Rezept konnte nicht erstellt werden');
    } finally {
      setGenerating(false);
    }
  };

  // ── Handlers: Manual mode ──

  const handleLookupIngredient = async () => {
    if (!ingName.trim()) return;
    setLookingUp(true);
    setError(null);
    try {
      const result = await post<{ ingredient: Ingredient }>('/ai/lookup-ingredient', {
        name: ingName.trim(),
        amount: ingAmount.trim() ? `${ingAmount.trim()}${ingUnit}` : undefined,
      });
      setIngredients(prev => [...prev, result.ingredient]);
      setIngName('');
      setIngAmount('');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Nährwert-Suche fehlgeschlagen');
    } finally {
      setLookingUp(false);
    }
  };

  const handleAddIngredientManual = () => {
    if (!ingName.trim() || !ingAmount.trim()) return;
    setIngredients(prev => [...prev, {
      name: ingName.trim(), amount: parseFloat(ingAmount) || 0, unit: ingUnit,
      kcal: 0, protein_g: 0, fat_g: 0, carbs_g: 0, fiber_g: 0,
      b12_mcg: 0, iron_mg: 0, vitd_mcg: 0, calcium_mg: 0, zinc_mg: 0, omega3_mg: 0, iod_mcg: 0,
    }]);
    setIngName('');
    setIngAmount('');
  };

  const addStep = () => {
    if (!stepText.trim()) return;
    setSteps(prev => [...prev, { number: prev.length + 1, instruction: stepText.trim(), timer_seconds: null, timer_label: null }]);
    setStepText('');
  };

  const handleManualSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await post('/recipes', {
        name: name.trim(), category, origin: 'manual',
        servings: parseInt(servings) || 2,
        prep_time_min: prepTime ? parseInt(prepTime) : null,
        ingredients: ingredients.length > 0 ? ingredients : undefined,
        steps: steps.length > 0 ? steps : undefined,
      });
      setResultRecipe({ name: name.trim(), kcal: totals.kcal, protein_g: totals.protein_g, servings: parseInt(servings) || 2 });
      setDone(true);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  // ── Reset ──
  const handleClose = () => {
    setMode('generate'); setName(''); setCategory('dinner'); setServings('2');
    setGenIngredients([]); setGenIngInput(''); setGenIngAmount(''); setStrictMode(false); setGenNotes('');
    setManualStep('basics'); setIngredients([]); setIngName(''); setIngAmount(''); setIngUnit('g');
    setSteps([]); setStepText(''); setPrepTime('');
    setError(null); setDone(false); setResultRecipe(null);
    onClose();
  };

  // ── Done ──
  if (done && resultRecipe) {
    return (
      <WizardModal open={open} onClose={handleClose} title="Rezept erstellt" footer={null}>
        <WizardSuccess message={`"${resultRecipe.name}" gespeichert!`} onDone={handleClose} />
        <div className="bg-white rounded-xl border border-warm-100 p-3 space-y-1">
          <div className="flex justify-between"><span className="text-sm text-warm-500">Kalorien</span><span className="text-sm font-medium text-warm-800">{formatNumber(resultRecipe.kcal)} kcal</span></div>
          <div className="flex justify-between"><span className="text-sm text-warm-500">Protein</span><span className="text-sm font-medium text-warm-800">{formatNumber(resultRecipe.protein_g)}g</span></div>
          <div className="flex justify-between"><span className="text-sm text-warm-500">Portionen</span><span className="text-sm font-medium text-warm-800">{resultRecipe.servings}</span></div>
        </div>
      </WizardModal>
    );
  }

  // ── Manual Step 3: Steps ──
  if (mode === 'manual' && manualStep === 'steps') {
    return (
      <WizardModal open={open} onClose={handleClose} title="Zubereitung (optional)"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setManualStep('ingredients')} className="flex-1 py-3 bg-warm-100 text-warm-600 rounded-xl font-medium text-sm">Zurück</button>
            <WizardButton onClick={handleManualSave} loading={saving}>Rezept speichern</WizardButton>
          </div>
        }
      >
        {error && <WizardError message={error} />}
        {steps.map((s, i) => (
          <div key={i} className="flex items-start gap-2 bg-warm-100 rounded-lg px-3 py-2">
            <span className="text-xs font-bold text-teal-600 mt-0.5">{s.number}.</span>
            <span className="text-sm text-warm-700 flex-1">{s.instruction}</span>
            <button onClick={() => setSteps(prev => prev.filter((_, j) => j !== i).map((s, j) => ({ ...s, number: j + 1 })))} className="text-warm-400 hover:text-coral-500 shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <textarea value={stepText} onChange={e => setStepText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addStep(); } }}
            placeholder="z.B. Zwiebeln in Öl anbraten..." rows={2}
            className="flex-1 px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none resize-none focus:ring-2 focus:ring-teal-400" />
          <button onClick={addStep} disabled={!stepText.trim()} className="px-3 py-2 bg-teal-400 text-white rounded-lg text-sm font-medium disabled:opacity-30 self-end">+</button>
        </div>
        <p className="text-[11px] text-warm-400 text-center">Schritte sind optional.</p>
      </WizardModal>
    );
  }

  // ── Manual Step 2: Ingredients ──
  if (mode === 'manual' && manualStep === 'ingredients') {
    return (
      <WizardModal open={open} onClose={handleClose} title="Zutaten"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setManualStep('basics')} className="flex-1 py-3 bg-warm-100 text-warm-600 rounded-xl font-medium text-sm">Zurück</button>
            <WizardButton onClick={() => setManualStep('steps')} disabled={ingredients.length === 0}>Weiter</WizardButton>
          </div>
        }
      >
        {error && <WizardError message={error} />}

        {ingredients.length > 0 && (
          <>
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-teal-50 text-teal-700 rounded-lg px-2 py-1.5 text-center"><p className="text-sm font-semibold">{formatNumber(totals.kcal)}</p><p className="text-[10px]">kcal</p></div>
              <div className="bg-sand-50 text-sand-700 rounded-lg px-2 py-1.5 text-center"><p className="text-sm font-semibold">{formatNumber(totals.protein_g)}g</p><p className="text-[10px]">Protein</p></div>
              <div className="bg-coral-50 text-coral-700 rounded-lg px-2 py-1.5 text-center"><p className="text-sm font-semibold">{formatNumber(totals.fat_g)}g</p><p className="text-[10px]">Fett</p></div>
              <div className="bg-warm-100 text-warm-700 rounded-lg px-2 py-1.5 text-center"><p className="text-sm font-semibold">{formatNumber(totals.carbs_g)}g</p><p className="text-[10px]">Carbs</p></div>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {ingredients.map((ing, i) => (
                <div key={i} className="flex items-center justify-between bg-warm-100 rounded-lg px-3 py-1.5">
                  <span className="text-sm text-warm-700">{ing.amount}{ing.unit} {ing.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-warm-400">{formatNumber(ing.kcal)} kcal</span>
                    <button onClick={() => setIngredients(prev => prev.filter((_, j) => j !== i))} className="text-warm-400 hover:text-coral-500">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div>
          <WizardLabel>Zutat hinzufügen</WizardLabel>
          <div className="flex gap-2 mt-1">
            <div className="flex-1 relative">
              <input type="text" value={ingName}
                onChange={e => { setIngName(e.target.value); searchFoodItems(e.target.value); }}
                onFocus={() => { if (foodSuggestions.length > 0) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleLookupIngredient(); } }}
                placeholder="Zutat suchen..." autoFocus
                className="w-full px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none focus:ring-2 focus:ring-teal-400" />
              {showSuggestions && foodSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-48 overflow-y-auto">
                  {foodSuggestions.map(item => (
                    <button key={item.id} onClick={() => selectFoodItem(item)}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b border-gray-50 last:border-0">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-gray-400 ml-2">{item.kcal_per_100} kcal/100g</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input type="number" value={ingAmount} onChange={e => setIngAmount(e.target.value)} placeholder="100" min="1"
              className="w-16 px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none focus:ring-2 focus:ring-teal-400" />
            <WizardSelect value={ingUnit} onChange={setIngUnit} options={[
              { value: 'g', label: 'g' }, { value: 'ml', label: 'ml' }, { value: 'Stück', label: 'Stk' },
              { value: 'EL', label: 'EL' }, { value: 'TL', label: 'TL' },
            ]} />
          </div>
          <p className="text-xs text-warm-400 mt-1">Tippe min. 2 Buchstaben um Vorschläge zu sehen. Klicke einen Vorschlag oder nutze die Buttons unten.</p>
          <div className="flex gap-2 mt-2">
            <button onClick={handleLookupIngredient} disabled={!ingName.trim() || lookingUp}
              className="flex-1 py-2 bg-teal-400 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {lookingUp ? 'Suche...' : 'Nicht gefunden? AI-Suche'}
            </button>
            <button onClick={handleAddIngredientManual} disabled={!ingName.trim() || !ingAmount.trim()}
              className="py-2 px-3 bg-warm-200 text-warm-600 rounded-lg text-sm font-medium disabled:opacity-50">
              Ohne Nährwerte
            </button>
          </div>
        </div>
      </WizardModal>
    );
  }

  // ── Main view: Mode tabs + Basics ──
  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Neues Rezept"
      footer={
        mode === 'generate' ? (
          <WizardButton onClick={handleGenerate} loading={generating} disabled={!name.trim()}>
            Rezept generieren
          </WizardButton>
        ) : (
          <WizardButton onClick={() => setManualStep('ingredients')} disabled={!name.trim()}>
            Weiter zu Zutaten
          </WizardButton>
        )
      }
    >
      {error && <WizardError message={error} />}

      {/* Mode Toggle */}
      <div className="flex bg-warm-100 rounded-lg p-0.5">
        <button onClick={() => setMode('generate')} className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${mode === 'generate' ? 'bg-white text-teal-600 shadow-sm' : 'text-warm-500'}`}>
          Generieren
        </button>
        <button onClick={() => setMode('manual')} className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${mode === 'manual' ? 'bg-white text-teal-600 shadow-sm' : 'text-warm-500'}`}>
          Manuell
        </button>
      </div>

      {/* Shared basics */}
      <div>
        <WizardLabel>Rezeptname</WizardLabel>
        <WizardInput value={name} onChange={setName}
          placeholder={mode === 'generate' ? 'z.B. Linsen-Dal, Pad Thai...' : 'z.B. Mein Lieblings-Curry'}
          autoFocus />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <WizardLabel>Kategorie</WizardLabel>
          <WizardSelect value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
        </div>
        <div>
          <WizardLabel>Portionen</WizardLabel>
          <WizardInput value={servings} onChange={setServings} type="number" min="1" max="8" />
        </div>
      </div>

      {/* Generate-specific: ingredients + notes */}
      {mode === 'generate' && (
        <>
          <div>
            <WizardLabel>Zutaten (optional)</WizardLabel>
            <p className="text-[11px] text-warm-400 mb-2">Rezept wird daraus erstellt. Mit Menge = Resteverwertung.</p>
            {genIngredients.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {genIngredients.map((ing, i) => (
                  <div key={i} className="flex items-center gap-2 bg-warm-200 rounded-lg px-3 py-1.5">
                    <span className="text-sm flex-1">{ing.name}{ing.amount ? <span className="text-warm-500 ml-1">({ing.amount})</span> : null}</span>
                    <button onClick={() => setGenIngredients(prev => prev.filter((_, j) => j !== i))} className="text-warm-400 hover:text-coral-500">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input type="text" value={genIngInput} onChange={e => setGenIngInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGenIngredient(); } }}
                placeholder="z.B. Kichererbsen"
                className="flex-1 px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none focus:ring-2 focus:ring-teal-400" />
              <input type="text" value={genIngAmount} onChange={e => setGenIngAmount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGenIngredient(); } }}
                placeholder="200g"
                className="w-20 px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none focus:ring-2 focus:ring-teal-400" />
              <button onClick={addGenIngredient} disabled={!genIngInput.trim()} className="px-3 py-2 bg-teal-400 text-white rounded-lg text-sm font-medium disabled:opacity-30">+</button>
            </div>
            {genIngredients.length > 0 && (
              <button onClick={() => setStrictMode(!strictMode)} className="w-full flex items-start gap-3 text-left mt-2">
                <div className={`mt-0.5 w-10 h-6 rounded-full transition-colors shrink-0 relative ${strictMode ? 'bg-coral-400' : 'bg-warm-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${strictMode ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-warm-800">Nur diese Zutaten</p>
                  <p className="text-[11px] text-warm-400 leading-tight">{strictMode ? 'Strikt: Keine zusätzlichen Zutaten' : 'Flexibel: Darf weitere Zutaten ergänzen'}</p>
                </div>
              </button>
            )}
          </div>
          <div>
            <WizardLabel>Zusätzliche Wünsche (optional)</WizardLabel>
            <textarea value={genNotes} onChange={e => setGenNotes(e.target.value)}
              placeholder="z.B. proteinreich, schnell, asiatisch..." rows={2}
              className="w-full mt-1 px-3 py-2 bg-warm-200 rounded-lg text-sm placeholder-warm-400 outline-none resize-none focus:ring-2 focus:ring-teal-400" />
          </div>
        </>
      )}

      {/* Manual-specific: prep time */}
      {mode === 'manual' && (
        <div>
          <WizardLabel>Zubereitungszeit (optional)</WizardLabel>
          <WizardInput value={prepTime} onChange={setPrepTime} placeholder="z.B. 30" type="number" min="1" />
          <p className="text-[11px] text-warm-400 mt-0.5">In Minuten</p>
        </div>
      )}
    </WizardModal>
  );
}
